import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { CallbackErrorAxios, CallbackWarningAxios, Client, CommonGlobalService, Option, RootState, UUID } from '@umanforge/common';
import { Dispatch } from '@reduxjs/toolkit';
import { ImportError } from 'interfaces/api_import.interfaces';
import { ClientService } from 'services/client.service';
import { isHeaderProblem } from 'utils/import.utils';



export function useImport( isOpen: boolean, importFunction: ( clientId: string, file: File, hasHeader: boolean | undefined, token: string | null, dispatch: Dispatch, onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios ) => Promise<UUID[] | ImportError | null>, onClose: ( isImported?: boolean ) => void ) {

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  //const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  /*----------------------------- useState ------------------------------*/
  const [ selectedClient, setSelectedClient ] = useState<Option<Client> | null>( null );
  const [ allowedClients, setAllowedClients ] = useState<Option<Client>[]>( [] );

  const [ selectedFile, setSelectedFile ] = useState<File | null>( null );

  const [ importError, setImportError ] = useState<ImportError | null>( null );

  const [ isOpenConfirmHeader, setIsOpenConfirmHeader ] = useState<boolean>( false );
  const [ canImport, setCanImport ] = useState<boolean>( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( true );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( isOpen ) {
      init();
    }
  }, [ isOpen ] );

  useEffect( () => {
    if ( allowedClients.length === 1 ) {
      setSelectedClient( allowedClients[ 0 ] );
    }
  }, [ allowedClients ] );

  useEffect( () => {
    setImportError( null );
    setCanImport( selectedClient != null && selectedFile != null );
  }, [ selectedClient, selectedFile ] );

  /*---------------------------- Functions ------------------------------*/
  const init = async () => {
    await refreshClients();
  };

  const openConfirmHeader = () => {
    setIsOpenConfirmHeader( true );
  };
  const closeConfirmHeader = () => {
    setIsOpenConfirmHeader( false );
  };

  const handleClose = () => {
    closeConfirmHeader();
    setSelectedClient( null );
    setSelectedFile( null );
    onClose();
  };

  const refreshClients = async () => {
    setIsLoading( true );
    const response = await ClientService.getAllowedClients( null, token, dispatch );
    const clients = CommonGlobalService.toOptions( response )
      .sort( ( client1, client2 ) => { return client1.label < client2.label ? -1 : 1; } );
    setAllowedClients( clients );
    setIsLoading( false );
  };

  const tryImportData = useCallback( async ( client: Client, file: File, hasHeader?: boolean ) => {
    setIsLoading( true );
    const response = await importFunction( client.id, file, hasHeader, token, dispatch );

    if ( Array.isArray( response ) ) {
      onClose( true );
    } else if ( response != null ) {
      if ( isHeaderProblem( response ) && hasHeader == null ) {
        openConfirmHeader();
      } else {
        setImportError( response );
      }
    }
    setIsLoading( false );
  }, [ token, dispatch ] );

  return {
    canImport,
    isLoading, setIsLoading,
    allowedClients,
    selectedClient, setSelectedClient,
    selectedFile, setSelectedFile,
    tryImportData,
    importError,
    isOpenConfirmHeader, closeConfirmHeader,
    handleClose
  };
}